import { useRef, useState } from 'react';
import {
  Box,
  Heading,
  Icon,
  InlineStack,
  Panel,
  PanelHeader,
  PanelInset,
  PanelSection,
  Tooltip,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import type { Customer } from '@/services/db/customer/entity';
import { searchCustomers, type CustomerSearchQuery } from '@/services/db/customer/merge';
import {
  PROFILE_TYPE_LABELS,
  ProfileType,
  type MergedAttributes,
  type MergeSummary,
  type MergeWorkspaceContext,
  type ProfileSource,
  type SelectedCustomers,
} from '../types';
import { mergedProfileSource } from '../utils/profile-attributes';
import { searchedEventProperties, selectableMatches } from '../utils/search';
import { CustomerProfileCard } from './customer-profile-card';
import { CustomerSearch, type SearchBy } from './customer-search';
import { CustomerSearchResults, type SearchResultsState } from './customer-search-results';
import * as styles from '../styles.css';

const SEARCH_FAILED_MESSAGE = 'Unable to search people. Try again.';

type SelectCustomerCardProps = {
  workspaceId: number;
  profileType: ProfileType;
  selected: SelectedCustomers;
  context: MergeWorkspaceContext;
  searchTerm: string;
  isLoadingMergedAttributes: boolean;
  mergedAttributes: MergedAttributes;
  mergeSummary: MergeSummary | undefined;
  /** Selects (or clears) this card's person; the merged card is read-only. */
  onSelect?: (customer: Customer | null) => void;
};

type CardHeadingProps = { profileType: ProfileType; terms: MergeWorkspaceContext['terms'] };

function CardHeading({ profileType, terms }: CardHeadingProps) {
  switch (profileType) {
    case ProfileType.Primary:
      return (
        <InlineStack alignBlock="center" gap="100">
          <Heading level="3" color="bold">
            Select a primary {terms.peopleSingular}
          </Heading>
          <Tooltip
            content={`When both ${terms.peoplePlural} contain a value for the same attribute, we keep the data of the primary ${terms.peopleSingular}.`}
          >
            <Icon name="help" color="subtle" display="block" />
          </Tooltip>
        </InlineStack>
      );
    case ProfileType.Secondary:
      return (
        <Heading level="3" color="bold">
          Select a {terms.peopleSingular} to merge into primary
        </Heading>
      );
    case ProfileType.Merged:
      return (
        <Heading level="3" color="bold">
          Merged {terms.peopleSingular}
        </Heading>
      );
    default: {
      const _exhaustive: never = profileType;
      return _exhaustive;
    }
  }
}

function selectedFor(profileType: ProfileType, selected: SelectedCustomers): Customer | null {
  return profileType === ProfileType.Secondary ? selected.secondary : selected.primary;
}

/**
 * One card's profile search: which mode it is in, the matches it found, and
 * selecting into or clearing this card. Matches already selected into either
 * card are filtered out, so the same person can never fill both sides.
 */
function useCardSearch({
  workspaceId,
  profileType,
  selected,
  onSelect,
}: Pick<SelectCustomerCardProps, 'workspaceId' | 'profileType' | 'selected' | 'onSelect'>) {
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const [searchBy, setSearchBy] = useState<SearchBy>('email');
  const [results, setResults] = useState<SearchResultsState>({ status: 'idle' });
  // A search already in flight wins; a second submission is dropped, not queued.
  const isSearchingRef = useRef(false);
  const pair = [selected.primary, selected.secondary];

  const search = async (query: CustomerSearchQuery) => {
    if (isSearchingRef.current) return;
    isSearchingRef.current = true;
    setResults({ status: 'searching' });
    try {
      const found = await searchCustomers(workspaceId, query);
      const { matches, matchCount } = selectableMatches(found, pair);
      void track('people', 'mergePersonSearched', searchedEventProperties(query, matchCount));
      setResults({ status: 'done', matches });
    } catch (error) {
      snackbars.create({ variant: 'critical', message: getUserFacingErrorMessage(error, SEARCH_FAILED_MESSAGE) });
      setResults({ status: 'idle' });
    } finally {
      isSearchingRef.current = false;
    }
  };

  return {
    searchBy,
    setSearchBy,
    search,
    clearSearch: () => setResults({ status: 'idle' }),
    selectCustomer: (match: Customer) => {
      void track('people', 'mergePersonSelected', { 'Profile Type': PROFILE_TYPE_LABELS[profileType] });
      onSelect?.(match);
    },
    selectAnother: () => {
      void track('people', 'mergePersonCleared', { 'Profile Type': PROFILE_TYPE_LABELS[profileType] });
      onSelect?.(null);
    },
    // A match that has since been selected into either card is no longer offered.
    visibleResults:
      results.status === 'done'
        ? ({ status: 'done', matches: selectableMatches(results.matches, pair).matches } satisfies SearchResultsState)
        : results,
  };
}

/**
 * One of the wizard's cards. Without a person it offers the search and its
 * results; with one it shows the profile. The merged card shows the preview
 * over the primary's identity.
 */
export function SelectCustomerCard({
  workspaceId,
  profileType,
  selected,
  context,
  searchTerm,
  isLoadingMergedAttributes,
  mergedAttributes,
  mergeSummary,
  onSelect,
}: SelectCustomerCardProps) {
  const { searchBy, setSearchBy, search, clearSearch, selectCustomer, selectAnother, visibleResults } = useCardSearch({
    workspaceId,
    profileType,
    selected,
    onSelect,
  });

  const customer = selectedFor(profileType, selected);
  const profile: ProfileSource | null =
    customer == null
      ? null
      : profileType === ProfileType.Merged
        ? mergedProfileSource(mergedAttributes, customer)
        : customer;

  return (
    <Panel
      backgroundColor={profileType === ProfileType.Secondary ? 'subtle' : undefined}
      withSectionBorders
      data-test="merge-select-customer"
      data-profile-type={profileType}
    >
      <PanelHeader display="flex" justifyContent="space-between">
        <Box flex={1}>
          <CardHeading profileType={profileType} terms={context.terms} />
        </Box>
      </PanelHeader>

      {profile != null ? (
        <Box data-test="people-merge-customer-profile" data-customer-id={customer?.id}>
          <CustomerProfileCard
            profileType={profileType}
            profile={profile}
            context={context}
            searchTerm={searchTerm}
            isLoadingMergedAttributes={isLoadingMergedAttributes}
            mergedAttributes={mergedAttributes}
            mergeSummary={mergeSummary}
            onSelectAnother={profileType === ProfileType.Merged ? undefined : selectAnother}
          />
        </Box>
      ) : (
        <>
          <PanelSection>
            <CustomerSearch
              name={`${profileType}-search-by`}
              searchBy={searchBy}
              onSearchByChange={setSearchBy}
              onSearch={(query) => void search(query)}
              onClearSearch={clearSearch}
            />
          </PanelSection>
          <PanelInset overflowY="auto" className={styles.searchResults}>
            <CustomerSearchResults
              results={visibleResults}
              canIdentifyByPhone={context.canIdentifyByPhone}
              peopleTitlePlural={context.terms.peopleTitlePlural}
              peoplePlural={context.terms.peoplePlural}
              onSelect={selectCustomer}
            />
          </PanelInset>
        </>
      )}
    </Panel>
  );
}
